import type { CSSProperties } from "react"
import type { SignalCategory } from "../types/api"
import "./FigmaSignalGlyphLibrary.css"

/**
 * Exact 24×24 signal masters exported by Micro App Crew Multi-Tools Design Package ZIPs.
 * Every SVG remains a separate source file. Vite resolves the original file URL;
 * no SVG path is reconstructed, rewritten, or substituted here.
 */
const RAW_SIGNAL_URLS = import.meta.glob("../assets/figma-signals/**/*.svg", {
  eager: true,
  query: "?url",
  import: "default",
}) as Record<string, string>

const EXACT_SIGNAL_URLS = new Map<string, string>()

for (const [path, url] of Object.entries(RAW_SIGNAL_URLS)) {
  const match = path.match(/figma-signals\/(urgent|road|attention)\/([^/]+)\.svg$/)
  if (match) EXACT_SIGNAL_URLS.set(`${match[1]}/${match[2]}`, url)
}

/**
 * Compatibility bridge between the legacy backend slugs already stored in DB
 * and the approved Figma asset slugs. This preserves signal_type ids/history
 * while the visual asset namespace follows the current 45-signal Figma catalog.
 */
const FIGMA_SLUG_BY_API_SLUG: Record<string, string> = {
  // Urgent
  urgent_contact_now: "srochno-svyazhites",
  car_being_towed: "mashinu-evakuiruyut",
  please_move_car: "perestavte-mashinu",
  blocking_passage: "meshaete-proezdu",
  blocking_unloading: "meshaete-razgruzke",
  blocking_emergency_vehicle: "meshaete-spectransportu",
  window_open: "otkryto-okno",
  door_open: "otkryta-dver",
  trunk_open: "otkryt-bagazhnik",
  lights_on: "vklyucheny-fary",
  alarm_triggered: "srabotala-signalizaciya",
  flat_tire: "spuscheno-koleso",
  car_problem: "problema-s-avtomobilem",
  kids_in_car: "deti-v-mashine",
  animal_in_car: "zhivotnoe-v-mashine",

  // Road
  cut_off: "podrezal",
  hard_braking: "rezko-tormozil",
  no_yield: "ne-ustupil",
  no_pedestrian_yield: "ne-propustil-peshehoda",
  drove_on_shoulder: "ehal-po-obochine",
  ran_red_light: "proehal-na-krasnyy",
  wrong_side: "vyehal-na-vstrechku",
  no_turn_signal: "ne-vklyuchil-povorotnik",
  no_lane_signal: "opasnoe-perestroenie",
  tailgating: "ne-derzhit-distanciyu",
  bad_parking: "nepravilno-priparkovalsya",
  parking_on_lawn: "necenzurnaya-bran",
  took_spot: "perekryl-vyezd",
  created_obstruction: "opasnaya-skorost",
  splashed_mud: "oblil-gryazyu",

  // Personal / attention
  thanks: "spasibo",
  thanks_for_help: "pomog-na-doroge",
  beep: "pobibikat",
  blink: "podmorgnut",
  saw_you_on_road: "videlis-na-doroge",
  good_luck: "udachi-na-doroge",
  countryman: "zemlyak",
  want_to_meet: "hochu-poznakomitsya",
  nice_car: "krasivaya-mashina",
  nice_plate: "krasivyy-nomer",
  nice_driver: "ulybnutsya",
  polite_driver: "vezhlivyy-voditel",
  careful_driver: "akkuratnyy-voditel",
  yielded_way: "ustupil-dorogu",
  good_sound: "horoshiy-zvuk",
}

const FIGMA_SIGNAL_COLOR_BY_KEY = new Map<string, string>()

function assignSignalColor(category: SignalCategory, slugs: readonly string[], token: string) {
  slugs.forEach((slug) => FIGMA_SIGNAL_COLOR_BY_KEY.set(`${category}/${slug}`, token))
}

// Final Small Signal Capsule export groups, 2026-08-31.
// Each group of five points to the exact bound Figma variable on its Icon Circle.
assignSignalColor("urgent", [
  "otkryto-okno", "otkryta-dver", "otkryt-bagazhnik", "vklyucheny-fary", "spuscheno-koleso",
], "var(--bb-figma-signal-red-1)")
assignSignalColor("urgent", [
  "srochno-svyazhites", "perestavte-mashinu", "meshaete-proezdu", "meshaete-razgruzke", "srabotala-signalizaciya",
], "var(--bb-figma-signal-red-2)")
assignSignalColor("urgent", [
  "mashinu-evakuiruyut", "meshaete-spectransportu", "problema-s-avtomobilem", "deti-v-mashine", "zhivotnoe-v-mashine",
], "var(--bb-figma-signal-red-3)")

assignSignalColor("road", [
  "necenzurnaya-bran", "ne-vklyuchil-povorotnik", "nepravilno-priparkovalsya", "oblil-gryazyu", "ne-derzhit-distanciyu",
], "var(--bb-figma-signal-yellow-1)")
assignSignalColor("road", [
  "podrezal", "rezko-tormozil", "ne-ustupil", "ne-propustil-peshehoda", "ehal-po-obochine",
], "var(--bb-figma-signal-yellow-2)")
assignSignalColor("road", [
  "proehal-na-krasnyy", "vyehal-na-vstrechku", "opasnoe-perestroenie", "perekryl-vyezd", "opasnaya-skorost",
], "var(--bb-figma-signal-yellow-3)")

assignSignalColor("attention", [
  "spasibo", "pobibikat", "podmorgnut", "ulybnutsya", "udachi-na-doroge",
], "var(--bb-figma-signal-green-1)")
assignSignalColor("attention", [
  "pomog-na-doroge", "vezhlivyy-voditel", "akkuratnyy-voditel", "ustupil-dorogu", "videlis-na-doroge",
], "var(--bb-figma-signal-green-2)")
assignSignalColor("attention", [
  "zemlyak", "hochu-poznakomitsya", "krasivaya-mashina", "krasivyy-nomer", "horoshiy-zvuk",
], "var(--bb-figma-signal-green-3)")

function signalKey(category: SignalCategory, slug?: string | null): string {
  const apiSlug = (slug || "").trim().toLowerCase()
  const figmaSlug = FIGMA_SLUG_BY_API_SLUG[apiSlug] || apiSlug
  return `${category}/${figmaSlug}`
}

export function exactFigmaSignalColor(category: SignalCategory, slug?: string | null): string | null {
  return FIGMA_SIGNAL_COLOR_BY_KEY.get(signalKey(category, slug)) || null
}

export function hasExactFigmaSignalGlyph(category: SignalCategory, slug?: string | null): boolean {
  return EXACT_SIGNAL_URLS.has(signalKey(category, slug))
}

export function FigmaSignalGlyph({ category, slug }: { category: SignalCategory; slug?: string | null }) {
  const src = EXACT_SIGNAL_URLS.get(signalKey(category, slug))
  if (!src) return null
  return (
    <span
      className="bbf-signal-glyph-master"
      style={{ "--bbf-signal-glyph-mask": `url("${src}")` } as CSSProperties}
      aria-hidden="true"
    />
  )
}
